/**
 * Raising Apple's native sign-in sheet on iOS and reporting what came back.
 *
 * Shared by the two things that need an Apple identity token: signing in
 * (`apple-sign-in-button`) and linking Apple to an account already signed in
 * (`link-social-account`). Only the better-auth call downstream differs, so
 * the plugin invocation, the nonce, and the "user dismissed the sheet" test
 * live here once — they are the parts that must not drift between the two.
 */

import type { ErrorCode } from "@capawesome/capacitor-apple-sign-in";
import * as Sentry from "@sentry/capacitor";
import { interfaceTranslator } from "@/i18n/translate";

/** Human-readable message for an error of unknown shape. */
function describeAuthError(error: unknown, fallback: string): string {
  if (typeof error === "string") {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

/**
 * The plugin rejects with a `code`: `SIGN_IN_CANCELED` when the user dismissed
 * the sheet, otherwise the numeric `ASAuthorizationError` as a string. The
 * message beside it is `localizedDescription`, rendered in the device's
 * language, so the code is what to branch on.
 */
const PLUGIN_CODE_CANCELED =
  "SIGN_IN_CANCELED" satisfies `${ErrorCode.SignInCanceled}`;

const NUMERIC_CODE_PATTERN = /^\d+$/u;

/**
 * What to show the user per `ASAuthorizationError` code. Apple's own
 * `localizedDescription` is the bare "operation couldn't be completed"
 * boilerplate plus a number, which tells a tester nothing they can act on.
 */
const APPLE_ERROR_KEYS = {
  // .unknown — raised before the sheet ever appears, so it is not the user's
  // action failing. Either the device has no Apple Account signed in, or the
  // running binary was signed without `com.apple.developer.applesignin`; the
  // release pipeline asserts the entitlement (ios/fastlane/Fastfile) so a
  // shipped build cannot be the second case silently.
  1000: "appleUnavailable",
  // .invalidResponse
  1002: "appleInvalidResponse",
  // .notHandled
  1003: "appleNotHandled",
  // .failed
  1004: "appleFailed",
  // .notInteractive
  1005: "appleNotInteractive",
} as const satisfies Record<number, string>;

/** The `code` a rejection carries, when it carries one. */
function pluginErrorCode(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("code" in error)) {
    return null;
  }
  const { code } = error;
  return typeof code === "string" && code.length > 0 ? code : null;
}

/** The `ASAuthorizationError` code a rejection's `code` names, if any. */
function appleErrorCode(pluginCode: string | null): number | null {
  return pluginCode !== null && NUMERIC_CODE_PATTERN.test(pluginCode)
    ? Number(pluginCode)
    : null;
}

/**
 * Report a sheet failure that isn't a cancel.
 *
 * A TestFlight build has no console, and this error surfaces only as a line of
 * red text on the sign-in screen that a tester has to retype into chat by hand
 * — which is how the entitlement case stayed invisible. Send the code so the
 * failure mode is readable in Sentry instead.
 *
 * `bucket` discriminates the fingerprint so each failure mode gets its own
 * issue: an authorization that returns no token is a different problem from a
 * rejection whose code could not be read, and burying one inside the other
 * hides whichever is rarer.
 */
function reportSheetFailure(
  bucket: string,
  code: number | null,
  pluginCode: string | null,
  message: string
): void {
  Sentry.captureException(
    new Error(`Apple sign-in sheet failed (${bucket}): ${message}`),
    {
      extra: { appleErrorCode: code, message, pluginErrorCode: pluginCode },
      // One issue per failure mode, not one per attempt.
      fingerprint: ["apple-sign-in-sheet-failed", bucket],
      tags: {
        appleErrorCode: String(code ?? "unknown"),
        area: "apple-sign-in",
        pluginErrorCode: pluginCode ?? "none",
      },
    }
  );
}

type AppleSheetResult =
  /** The user dismissed the sheet. Not a failure — say nothing and stop. */
  | { status: "canceled" }
  | { message: string; status: "error" }
  | {
      /** The stable Apple user identifier, the token's `sub`. */
      appleUserId: string;
      /** Apple returns these only on the FIRST authorization for an account. */
      familyName?: string | null;
      givenName?: string | null;
      /** Generated here and passed to better-auth so it can bind the token. */
      nonce: string;
      status: "ok";
      token: string;
    };

/**
 * Raises the sheet and returns the identity token.
 *
 * This authenticates through the app's own bundle ID (verified via entitlements
 * + the provisioning profile), NOT a Services ID: the plugin's iOS
 * implementation never reads one. Server-side, better-auth validates the
 * resulting token's audience against `appBundleIdentifier`, not `clientId` —
 * see the comment on that field in apps/api/src/utils/auth.ts.
 *
 * iOS only; callers gate on `Capacitor.getPlatform() === "ios"`.
 */
export async function authorizeWithAppleSheet(
  fallbackMessage: string
): Promise<AppleSheetResult> {
  try {
    const { AppleSignIn, SignInScope } = await import(
      "@capawesome/capacitor-apple-sign-in"
    );
    const nonce = crypto.randomUUID();
    const { familyName, givenName, idToken, user } = await AppleSignIn.signIn({
      nonce,
      scopes: [SignInScope.Email, SignInScope.FullName],
    });

    if (!idToken) {
      // The sheet came back without the one thing it exists to return. That
      // sentence names an Apple internal a host cannot act on, so it goes to
      // Sentry and the reader gets the caller's wording, as the catch below
      // does for an unmapped code.
      reportSheetFailure(
        "no-identity-token",
        null,
        null,
        "No identity token received from Apple"
      );
      return { message: fallbackMessage, status: "error" };
    }
    return {
      appleUserId: user,
      familyName,
      givenName,
      nonce,
      status: "ok",
      token: idToken,
    };
  } catch (error) {
    const pluginCode = pluginErrorCode(error);
    if (pluginCode === PLUGIN_CODE_CANCELED) {
      return { status: "canceled" };
    }
    const code = appleErrorCode(pluginCode);
    const rawMessage = describeAuthError(error, fallbackMessage);
    // A string code that is not an ASAuthorizationError (Capacitor's
    // `UNIMPLEMENTED`, say) names its own failure mode.
    const bucket = String(code ?? pluginCode ?? "unreadable-code");
    reportSheetFailure(bucket, code, pluginCode, rawMessage);
    const knownKey =
      code === null
        ? undefined
        : APPLE_ERROR_KEYS[code as keyof typeof APPLE_ERROR_KEYS];
    const knownMessage =
      knownKey && interfaceTranslator("auth.appleNativeSheet")(knownKey);
    // An unmapped code falls back to the caller's wording, never to Apple's
    // own: `localizedDescription` renders as "The operation couldn't be
    // completed. (com.apple.AuthenticationServices.AuthorizationError error
    // 1001.)", which tells a host nothing and reads as a crash. The raw text is
    // already on its way to Sentry above, which is where it is useful.
    return { message: knownMessage ?? fallbackMessage, status: "error" };
  }
}
